// Seguridad — the webapp's password change and active sessions (web-only). A wrong current password
// answers 401 on this server: the worker reads that RAW so it never signs the person out (see
// accountChangePassword). The session this tab runs on is marked by its CSRF token and cannot be
// ended from the list (the sign-out button does that, honestly named).
import { useEffect, useState } from 'react';
import { data } from '../../../bridge/client';
import { passwordProblem, type SessionRow } from '../../../core/accountSettings';
import { Icon, type IconName } from '../../../design/icons';
import { locale, t, tf } from '../../../i18n/i18n';
import { Button, ConfirmButton, InlineStatus } from '../../kit/Kit';
import { SaveStatus, SettingsGroup, useAccount, useSaver } from './parts';
import { useHour12 } from './StatusSection';

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const [k, ...v] = part.trim().split('=');
    if (k === name) return decodeURIComponent(v.join('='));
  }
  return null;
}

const KIND_ICON: Record<SessionRow['kind'], IconName> = { browser: 'globe', mobile: 'phone', desktop: 'sidebar', other: 'person' };

export function SecuritySection({ onSignOut }: { onSignOut?(): void }) {
  const { account } = useAccount();
  return (
    <>
      {account?.user.authService ? (
        <SettingsGroup title={t('web.settings.security.password')}>
          <div className="list-row">
            <span className="list-row-text">
              <span className="list-row-subtitle">{tf('web.settings.security.sso', account.user.authService.toUpperCase())}</span>
            </span>
          </div>
        </SettingsGroup>
      ) : (
        <PasswordForm />
      )}
      <Sessions />
      {onSignOut ? <SignOutEverywhere onSignOut={onSignOut} /> : null}
    </>
  );
}

/**
 * §41.10 «Cerrar sesión en todos los dispositivos»: confirm (the button arms, a second click does
 * it), `POST /users/me/sessions/revoke/all`, then this tab's normal sign-out wipe. A refusal keeps
 * the reader signed in and says so.
 */
function SignOutEverywhere({ onSignOut }: { onSignOut(): void }) {
  const saver = useSaver();
  return (
    <SettingsGroup title={t('web.security.signOutAll')} save={saver.state} footer={t('web.security.signOutAllHint')}>
      <div className="list-row st-session-all">
        <ConfirmButton
          icon="signOut"
          label={t('web.security.signOutAll')}
          confirmLabel={t('web.security.signOutAllConfirm')}
          onConfirm={() =>
            saver.run(
              async () => {
                const ok = await data.call('accountRevokeAllSessions');
                if (ok) onSignOut();
                return ok;
              },
              undefined,
              t('web.security.signOutAllFailed'),
            )
          }
        />
      </div>
    </SettingsGroup>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const saver = useSaver();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const p = passwordProblem(current, next, confirm);
    if (p) {
      setProblem(t(p === 'mismatch' ? 'web.settings.security.mismatch' : p === 'same' ? 'web.settings.security.same' : 'web.settings.security.missing'));
      return;
    }
    setProblem(null);
    const [c, n] = [current, next];
    saver.run(async () => {
      const r = await data.call('accountChangePassword', c, n);
      if (r.ok) {
        setCurrent('');
        setNext('');
        setConfirm('');
        return true;
      }
      setProblem(
        r.reason === 'wrongCurrent' ? t('web.settings.security.wrongCurrent') : r.reason === 'network' ? t('web.settings.security.network') : r.message || t('web.settings.security.rejected'),
      );
      return false;
    });
  };
  const state = saver.state.kind === 'error' ? { kind: 'idle' as const } : saver.state;
  return (
    <SettingsGroup title={t('web.settings.security.password')}>
      <form className="st-form" onSubmit={submit}>
        <input type="text" autoComplete="username" hidden readOnly value="" />
        <label className="st-field">
          <span>{t('web.settings.security.current')}</span>
          <input className="text-input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label className="st-field">
          <span>{t('web.settings.security.new')}</span>
          <input className="text-input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </label>
        <label className="st-field">
          <span>{t('web.settings.security.confirm')}</span>
          <input className="text-input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </label>
        <div className="st-form-actions">
          {problem ? (
            <span className="st-save st-save-error" role="alert">
              {problem}
            </span>
          ) : saver.state.kind === 'saved' ? (
            <span className="st-save st-save-saved" role="status">
              <Icon name="check" size={13} strokeWidth={2.4} />
              {t('web.settings.security.changed')}
            </span>
          ) : (
            <SaveStatus state={state} />
          )}
          <Button kind="primary" type="submit" disabled={saver.state.kind === 'saving' || !current || !next || !confirm}>
            {t('web.settings.security.change')}
          </Button>
        </div>
      </form>
    </SettingsGroup>
  );
}

function Sessions() {
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const saver = useSaver();
  const hour12 = useHour12();
  useEffect(() => {
    let alive = true;
    setFailed(false);
    data.call('accountSessions', readCookie('MMCSRF')).then(
      (r) => {
        if (!alive) return;
        if (r) setRows(r);
        else setFailed(true);
      },
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [attempt]);

  const revoke = (ids: string[]) => {
    if (!rows) return;
    setRows(rows.filter((r) => !ids.includes(r.id)));
    saver.run(
      async () => {
        const results = await Promise.all(ids.map((id) => data.call('accountRevokeSession', id)));
        return results.every(Boolean);
      },
      () => setAttempt((n) => n + 1),
      t('web.settings.security.revokeFailed'),
    );
  };

  const when = (ms: number) => (ms ? new Intl.DateTimeFormat(locale(), { dateStyle: 'medium', timeStyle: 'short', hour12 }).format(ms) : '');
  const others = rows?.filter((r) => !r.current) ?? [];

  return (
    <SettingsGroup title={t('web.settings.security.sessions')} save={saver.state} footer={t('web.settings.security.sessionsHint')}>
      {failed ? (
        <div className="list-row">
          <InlineStatus kind="error" text={t('web.settings.security.sessionsFailed')} onRetry={() => setAttempt((n) => n + 1)} />
        </div>
      ) : !rows ? (
        <div className="list-row st-muted">…</div>
      ) : (
        <>
          {rows.map((r) => (
            <div key={r.id} className="list-row st-session">
              <span className="list-row-leading">
                <Icon name={KIND_ICON[r.kind]} size={18} />
              </span>
              <span className="list-row-text">
                <span className="list-row-title">
                  {r.device || t('web.settings.security.unrecognized')}
                  {r.current ? <span className="st-tag">{t('web.settings.security.thisSession')}</span> : null}
                </span>
                <span className="list-row-subtitle">
                  {capitalize(
                    [
                      r.kind === 'other' ? '' : t(`web.settings.security.kind.${r.kind}`),
                      r.lastActivityAt ? tf('web.settings.security.lastActive', when(r.lastActivityAt)) : '',
                    ]
                      .filter(Boolean)
                      .join(' · '),
                  )}
                </span>
              </span>
              {!r.current ? (
                <span className="list-row-trailing">
                  <ConfirmButton label={t('web.settings.security.revoke')} confirmLabel={t('web.settings.security.revokeConfirm')} onConfirm={() => revoke([r.id])} />
                </span>
              ) : null}
            </div>
          ))}
          {others.length > 1 ? (
            <div className="list-row st-session-all">
              <ConfirmButton icon="signOut" label={t('web.settings.security.revokeOthers')} confirmLabel={t('web.settings.security.revokeOthersConfirm')} onConfirm={() => revoke(others.map((r) => r.id))} />
            </div>
          ) : !others.length ? (
            <div className="list-row">
              <span className="list-row-subtitle">{t('web.settings.security.noOthers')}</span>
            </div>
          ) : null}
        </>
      )}
    </SettingsGroup>
  );
}

function capitalize(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}
